import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";
import { ActiveVoteInterface } from "@/appInterface/windows/ActiveVote/ActiveVoteInterface";
import { CurrentVoteInfo } from "@/appInterface/windows/ActiveVote/CurrentVoteInfo";
import {
    getAddDurationEventData,
    getAddOrRemoveVoteEventData,
    getFinishEventData,
    getVoteStats,
} from "@/appInterface/windows/ActiveVote/helpers";
import { EndVote } from "@/appInterface/windows/VoteWindow/EndVote";
import type { VoteEntity } from "@/appInterface/windows/VoteWindow/VoteResults/types";
import style from "@/appInterface/windows/VoteWindow/VoteWindow.module.css";
import { canv } from "@/canv/global";
import { eventEmitter } from "@/eventEmitter";
import type { Group } from "@/group";

export class ActiveVote {
    open = () => {
        this.isOpen = true;
        this.data = canv.canvasController.activeVote;

        if (!this.data) return;

        this.createInterface();
        this.setUserInfo();
        this.updateVotesInfo();

        this.createInfoModal();

        eventEmitter.dispatch("vote.active.visibility", { isVisible: true });
    };

    close = () => {
        this.isOpen = false;
        this.data = null;

        this.resetUserInfo();
        this.resetModalData();
        this.resetInterface();

        eventEmitter.dispatch("vote.active.visibility", { isVisible: false });
    };

    update = () => {
        if (!canv.canvasController.activeVote) return;
        this.data = canv.canvasController.activeVote as VoteEntity;
        this.updateVotesInfo();
        this.updateVoteModal();
        if (this.data?.isResolved) {
            // todo логика связанная с завершением голосования
        }
    };

    setUserInfo() {
        this.isCreator = this.data?.creatorToken === canv.getInitDataProp("userToken");
        this.userToken = canv.getInitDataProp("userToken");
        this.accountName = canv.getInitDataProp("userName");
    }

    resetUserInfo() {
        this.isCreator = false;
        this.accountName = "";
        this.userToken = "";
    }

    createInterface() {
        this.interface = new ActiveVoteInterface(this);
    }

    resetInterface() {
        this.interface?.remove();
        this.interface = null;
    }

    resetCurrentVoteInfo = () => {
        this.infoModal?.unmount();
        this.infoModal = null;

        if (this.infoModalRoot) {
            document.body.removeChild(this.infoModalRoot);
            this.infoModalRoot = null;
        }

        if (this.endModal) {
            this.closeEndModal();
        }

        this.close();
    };

    renderVoteModal(container: HTMLElement | null) {
        if (!this.data) return;

        const element = createElement(CurrentVoteInfo, {
            onClose: this.resetCurrentVoteInfo,
            vote: {
                duration: this.data.duration,
                text: this.data.text,
                oneVotePerItem: this.data.oneVotePerItem,
                start: this.data.start,
                availableVotesCount: this.availableVotesCount,
            },
            isCreator: this.isCreator,
            addTimerDuration: this.addTimerDuration,
            finishVote: this.finishVote,
        });

        if (container && this.infoModal) {
            this.infoModal.render(element);
        }
    }

    closeEndModal = () => {
        this.endModal?.unmount();
        this.endModal = null;

        if (this.endModalRoot) {
            document.body.removeChild(this.endModalRoot);
            this.endModalRoot = null;
        }
    };

    renderEndModal(container: HTMLElement | null) {
        if (!this.data) return;

        const element = createElement(EndVote, {
            endHandler: () => {
                canv.intercom.eventMessage(getFinishEventData(this.data?.UID || ""));
                this.closeEndModal();
            },
            closeModal: () => {
                this.closeEndModal();
                this.open();
            },
        });

        if (container && this.endModal) {
            this.endModal.render(element);
        }
    }

    createInfoModal() {
        if (this.infoModal || !this.data) return;

        const infoModalDiv = document.createElement("div");
        infoModalDiv.id = "currentInfoModal";
        infoModalDiv.classList.add(style.currentInfoModal);

        this.infoModalRoot = infoModalDiv;
        this.infoModal = createRoot(infoModalDiv);

        this.renderVoteModal(infoModalDiv);
        document.body.appendChild(infoModalDiv);
    }

    createEndModal() {
        if (this.endModal || !this.data) return;

        const endModal = document.createElement("div");
        endModal.id = "endModal";
        endModal.classList.add(style.endModal);

        this.endModalRoot = endModal;
        this.endModal = createRoot(endModal);

        this.renderEndModal(endModal);
        document.body.appendChild(endModal);
    }

    updateVoteModal() {
        if (!this.infoModal || !this.data) return;
        this.updateVotesInfo();

        this.renderVoteModal(this.infoModalRoot);
    }

    resetModalData() {
        this.availableVotesCount = 0;
        this.myVotesCastCount = 0;
        this.infoModal = null;
        this.infoModalRoot = null;
        this.endModal = null;
        this.endModalRoot = null;
    }

    addVoteToWidget(widgetUID: string) {
        if (!this.data || this.availableVotesCount <= 0) return;

        canv.intercom.eventMessage(
            getAddOrRemoveVoteEventData(
                this.data.UID,
                widgetUID,
                "add",
                this.userToken,
                this.accountName,
            ),
        );
    }

    removeVoteFromWidget(widgetUID: string) {
        if (!this.data || this.myVotesCastCount === 0) return;

        canv.intercom.eventMessage(
            getAddOrRemoveVoteEventData(
                this.data.UID,
                widgetUID,
                "remove",
                this.userToken,
                this.accountName,
            ),
        );
    }

    updateVotesInfo = () => {
        let availableVotesCount = 0;

        if (this.data) {
            const { myVoteCount, totalCandidates } = getVoteStats(
                this.data,
                canv.getInitDataProp("userToken"),
            );
            if (this.data.oneVotePerItem) {
                availableVotesCount = totalCandidates - myVoteCount;
            } else {
                availableVotesCount = this.data.votePerAccount - myVoteCount;
            }

            this.availableVotesCount = Math.max(availableVotesCount, 0);
            this.myVotesCastCount = myVoteCount;
        }
    };

    addTimerDuration = (duration: number) => {
        if (!this.data) return;
        canv.intercom.eventMessage(getAddDurationEventData(this.data.UID, duration));
    };

    finishVote = () => {
        if (!this.isCreator || !this.data) return;

        this.infoModal?.unmount();
        this.infoModal = null;

        if (this.infoModalRoot) {
            document.body.removeChild(this.infoModalRoot);
            this.infoModalRoot = null;
        }

        this.createEndModal();
    };

    drawOver(group: Group) {
        if (
            this?.interface?.needDrawTooltip &&
            group.children[0].UID === this.interface.hoveredWidget
        ) {
            this.interface.drawToolTip(group.startX + group.width, group.startY + group.height);
        }
    }

    isOpen = false;
    data: VoteEntity | null = null;

    endModal: Root | null = null;
    endModalRoot: HTMLDivElement | null = null;
    infoModal: Root | null = null;
    infoModalRoot: HTMLDivElement | null = null;

    availableVotesCount = 0;
    myVotesCastCount = 0;

    isCreator = false;
    userToken = "";
    accountName = "";

    interface: ActiveVoteInterface | null = null;
}
